import { useCallback, useState } from 'react';

interface UseRetryProps {
    maxTimes?: number;
    retryDelay?: number;
    onRetry?: (attempt: number) => void;
    onMaxTimesReached?: () => void;
}

interface UseRetry {
    asyncActionWithRetry: <T>(fn: () => Promise<T>) => Promise<T>;
    retryCount: number;
    isRetrying: boolean;
    reset: () => void;
}

export const useRetry = (options: UseRetryProps): UseRetry => {
    const { maxTimes = 2, retryDelay = 1000, onRetry, onMaxTimesReached } = options;

    const [retryCount, setRetryCount] = useState(0);
    const [isRetrying, setIsRetrying] = useState(false);

    const reset = useCallback(() => {
        setRetryCount(0);
        setIsRetrying(false);
    }, []);

    const asyncActionWithRetry = useCallback(
        async <T>(fn: () => Promise<T>): Promise<T> => {
            let lastError: Error | null = null;

            for (let attempt = 0; attempt <= maxTimes; attempt++) {
                try {
                    if (attempt > 0) {
                        setIsRetrying(true);
                        setRetryCount(attempt);
                        onRetry?.(attempt);

                        if (retryDelay > 0) {
                            await new Promise((resolve) => setTimeout(resolve, retryDelay));
                        }
                    }

                    const result = await fn();
                    setIsRetrying(false);
                    return result;
                } catch (error) {
                    lastError = error instanceof Error ? error : new Error(String(error));

                    if (attempt === maxTimes) {
                        setIsRetrying(false);
                        onMaxTimesReached?.();
                        throw lastError;
                    }
                }
            }

            throw lastError || new Error('Unknown error occurred');
        },
        [maxTimes, retryDelay, onRetry, onMaxTimesReached]
    );

    return {
        asyncActionWithRetry,
        retryCount,
        isRetrying,
        reset
    };
};
